import React, {
  useContext,
  useState,
  FormEvent,
  useEffect,
  useRef,
} from 'react';
import { addRecentSearch } from 'src/areas/search-suggestions/history-storage';
import { AppContext } from '@prisjakt/ui-app-context';
import { BodyMediumText } from '@prisjakt/ui/text/variants';
import { Row } from '@prisjakt/ui/list/variants';

import {
  StyledSearchBar,
  InputWrapper,
  SearchInput,
  ClearIcon,
  ClearButton,
  SearchIcon,
  SearchButton,
  SearchButtonText,
  Text,
  SearchBarWrapper,
  SearchBarGroup,
  CloseButton,
} from './styles';
import { useHistory, useLocation } from 'react-router-dom';
import Suggestions from 'src/areas/search-suggestions';
import { IconChevronLeft } from '@prisjakt/ui/icon';
import { useEventListener } from 'src/hooks/use-event-listener';
import NoScroll from '../no-scroll';
import { useSearchBarContext } from './search-bar-context';
import { connect } from 'react-redux';
import searchActions from '../../areas/search/actions/search';
import { trackSearchCleared } from '../../areas/search/tracking';

type SearchBarProps = {
  query?: string;
  focused?: boolean;
  selected?: number;
  disabled?: boolean;
  visible?: boolean;
};

type DispatchProps = {
  searchFocus: () => void;
  searchBlur: () => void;
};

const SearchBar = (props: SearchBarProps & DispatchProps) => {
  const {
    value,
    setValue,
    active,
    setActive,
    focused,
    setFocused,
  } = useSearchBarContext();
  const {
    query = '',
    selected,
    disabled = false,
    visible = false,
    searchFocus,
    searchBlur,
  } = props;
  const { t } = useContext(AppContext);
  const searchBarRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const location = useLocation();

  const [currentQuery, setCurrentQuery] = useState(query);

  const searchCriteria = currentQuery.length >= 2;

  const handleChange = (e: FormEvent<HTMLInputElement>) => {
    const newValue = (e.target as HTMLInputElement).value;
    setCurrentQuery(newValue);

    if (searchCriteria && !active) {
      setActive(true);
    }
  };

  useEffect(() => {
    if (!location.pathname.includes('/search')) {
      setCurrentQuery('');
      return;
    }

    const url = location.search;
    const searchParam = new URLSearchParams(url).get('search') || '';
    setCurrentQuery(searchParam);
  }, [location]);

  useEffect(() => {
    if (!searchBarRef.current) {
      return;
    }

    if (!visible) {
      setValue(currentQuery);
      return;
    }

    if (focused) {
      searchBarRef.current.focus();
    }
  }, [focused, searchBarRef, visible]);

  useEffect(() => {
    if (value !== currentQuery) {
      setCurrentQuery(value);
    }
  }, [value]);

  const handleSubmit = (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();

    if (disabled || !searchCriteria) {
      return;
    }
    addRecentSearch(currentQuery);
    setFocused(false);
    setActive(false);
    history.push(`/search?search=${encodeURIComponent(currentQuery)}`);
  };

  const handleClearSearchClick = () => {
    setCurrentQuery('');
    focusSearchBar();
    trackSearchCleared();
  };

  const blurSearchBar = () => {
    setActive(false);
    setFocused(false);
    searchBlur();
  };

  const focusSearchBar = () => {
    if (!searchBarRef.current) {
      return;
    }

    searchBarRef.current.focus();

    if (!active) {
      setActive(true);
      searchFocus();
    }
  };

  useEventListener('focus-search-event', (e: CustomEvent) => {
    if (!e.detail.focus || !visible || !searchBarRef.current) {
      return;
    }

    focusSearchBar();
  });

  useEventListener('keyup', (e: KeyboardEvent) => {
    const key = e.key || e.keyCode;
    const isTabKey = key === 'Tab' || key === 9;
    const isEscKey = key === 'Escape' || key === 'Esc' || key === 27;
    if (isTabKey || isEscKey) {
      setActive(false);
    }
  });

  return (
    <>
      <SearchBarWrapper $active={active}>
        <SearchBarGroup $active={active} $visible={visible}>
          <CloseButton $visible={active} onClick={() => blurSearchBar()}>
            <IconChevronLeft size={30} />
          </CloseButton>
          <StyledSearchBar $active={active}>
            <InputWrapper data-suggestions="searchWrapper">
              <BodyMediumText>
                <form action="/search" onSubmit={handleSubmit} role="search">
                  <SearchInput
                    ref={searchBarRef}
                    value={currentQuery}
                    data-suggestions="search"
                    role="combobox"
                    aria-expanded={focused && active}
                    aria-owns={
                      focused || active ? 'overlaySearchResults' : undefined
                    }
                    aria-autocomplete="both"
                    aria-activedescendant={
                      selected ? `selection${selected}` : ''
                    }
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    type="search"
                    placeholder={t('search', 'search.placeholder')}
                    onChange={handleChange}
                    maxLength={256}
                    onFocus={() => setFocused(true)}
                    onClick={() => focusSearchBar()}
                  />
                </form>
              </BodyMediumText>

              <ClearButton
                show={currentQuery.length > 0}
                data-test="ClearSearchButton"
                data-testid="clear-search-button"
                onClick={() => handleClearSearchClick()}
              >
                <ClearIcon size={12} />
              </ClearButton>
            </InputWrapper>

            <SearchButton
              disabled={!searchCriteria}
              active={active && visible}
              onClick={handleSubmit}
              data-test="SearchButton"
              type="button"
              aria-label={t('header', 'search.button')}
            >
              <SearchButtonText>
                <Row gap={0.5}>
                  <SearchIcon size={20} />
                  <Text $visible={searchCriteria}>
                    {t('header', 'search.button')}
                  </Text>
                </Row>
              </SearchButtonText>
            </SearchButton>
          </StyledSearchBar>
        </SearchBarGroup>

        <Suggestions
          visible={active && visible}
          query={currentQuery}
          setQuery={newQuery => setCurrentQuery(newQuery)}
          onClose={() => blurSearchBar()}
          updatedInputElement={null}
          updatedSearchWrapperElement={null}
        />
      </SearchBarWrapper>
      <NoScroll mobile enabled={active && visible} />
    </>
  );
};

const dispatchActions = {
  searchFocus: searchActions.searchFocus,
  searchBlur: searchActions.searchBlur,
};

export default connect(null, dispatchActions)(SearchBar);
